import { Box, Proportion } from '@wix/design-system';
import _ from 'lodash';
import { FC, useMemo, useCallback } from 'react';
import Project from '../Project';
import { PortfolioItemWithUrl } from '../Project/Project';
import { PROFILE_CLIENT_PROJECTS_LAYOUT_TYPE } from '@utils/constants';
import { useProfilePageBi } from '@components/Hooks/useProfilePageBi';
import s from './ProjectGrid.module.scss';

const STANDARD_ROW_PROJECT_COUNT = 3;
const SECOND_ROW_PROJECT_COUNT = 2;

const ROW_PATTERN = [
  STANDARD_ROW_PROJECT_COUNT,
  SECOND_ROW_PROJECT_COUNT,
  STANDARD_ROW_PROJECT_COUNT,
];

type Props = {
  projects: PortfolioItemWithUrl[];
  quantityToDisplay: number;
  isMobileView?: boolean;
  onProjectClick?: (project: PortfolioItemWithUrl) => void;
};

const handleProjectClick = (
  portfolioItem: PortfolioItemWithUrl,
  onProjectClick?: (project: PortfolioItemWithUrl) => void,
  profilePageBi?: ReturnType<typeof useProfilePageBi>,
) => {
  if (profilePageBi) {
    profilePageBi.logProfilePageClick(
      'go to project page',
      JSON.stringify({ metaSiteId: portfolioItem.metaSiteId }),
    );

    if (onProjectClick) {
      onProjectClick(portfolioItem);
    }
  }
};

const handleProjectKeyDown = (
  event: React.KeyboardEvent,
  portfolioItem: PortfolioItemWithUrl,
  onProjectClick?: (project: PortfolioItemWithUrl) => void,
  profilePageBi?: ReturnType<typeof useProfilePageBi>,
) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    handleProjectClick(portfolioItem, onProjectClick, profilePageBi);
  }
};

const ProjectGrid: FC<Props> = ({
  projects,
  quantityToDisplay,
  isMobileView,
  onProjectClick,
}) => {
  const profilePageBi = useProfilePageBi();
  const projectsToShow = projects.slice(0, quantityToDisplay);

  const renderProjectButton = useCallback(
    (
      portfolioItem: PortfolioItemWithUrl,
      layoutType?: 'twoColumn' | 'threeColumn',
    ) => (
      <Proportion
        key={portfolioItem.metaSiteId}
        aspectRatio={1.778}
        className={s.proportion}
      >
        <div
          className={s.projectRowItem}
          role="button"
          tabIndex={0}
          aria-label={`View project ${portfolioItem.siteName}`}
          onClick={() =>
            handleProjectClick(portfolioItem, onProjectClick, profilePageBi)
          }
          onKeyDown={(event: React.KeyboardEvent) => {
            handleProjectKeyDown(
              event,
              portfolioItem,
              onProjectClick,
              profilePageBi,
            );
          }}
        >
          <Project
            {...portfolioItem}
            isMobileView={isMobileView}
            layoutType={layoutType}
          />
        </div>
      </Proportion>
    ),
    [onProjectClick, profilePageBi, isMobileView],
  );

  const rows = useMemo(() => {
    if (isMobileView) return [];

    const computedRows: PortfolioItemWithUrl[][] = [];
    let currentIndex = 0;
    let patternIndex = 0;

    while (currentIndex < projectsToShow.length) {
      // Use pattern for first 3 rows, then default to standard size
      const rowSize =
        patternIndex < ROW_PATTERN.length
          ? ROW_PATTERN[patternIndex]
          : STANDARD_ROW_PROJECT_COUNT;

      const endIndex = Math.min(currentIndex + rowSize, projectsToShow.length);
      computedRows.push(projectsToShow.slice(currentIndex, endIndex));

      currentIndex = endIndex;
      patternIndex++;
    }

    return computedRows;
  }, [projectsToShow, isMobileView]);

  if (isMobileView) {
    return (
      <div className={s.projectsMobile}>
        {_.map(projectsToShow, (portfolioItem) =>
          renderProjectButton(portfolioItem),
        )}
      </div>
    );
  }

  return (
    <Box className={s.projectGrid} direction="vertical">
      {rows.map((row, rowIndex) => {
        const isThreeColumnRow = rowIndex !== 1;
        const layoutType = isThreeColumnRow
          ? PROFILE_CLIENT_PROJECTS_LAYOUT_TYPE.THREE_COLUMN
          : PROFILE_CLIENT_PROJECTS_LAYOUT_TYPE.TWO_COLUMN;

        return (
          <Box
            key={rowIndex}
            className={`${s.projectRow} ${
              rowIndex === 1 ? s.twoProjectRow : s.threeProjectRow
            }`}
            direction="horizontal"
          >
            {row.map((portfolioItem) =>
              renderProjectButton(portfolioItem, layoutType),
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default ProjectGrid;
